import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { Form, Link, redirect, useActionData, useLoaderData, useNavigation } from 'react-router';
import type { ActionFunctionArgs } from 'react-router';
import { ApiError, api } from '../lib/api';
import AuthScene from '../components/AuthScene';
import { clearSignupIntent, pricingPathFor, readSignupIntent } from '../lib/signupIntent';
import FormError from '../components/FormError';
import type { AuthUser, OnboardingPayload } from '../lib/types';

interface WelcomeLoaderData {
  user: AuthUser;
  canEditStudio: boolean;
}

interface WelcomeActionData {
  error?: string;
}

interface Choice {
  id: string;
  label: string;
  hint?: string;
}

const USER_TYPES: Choice[] = [
  { id: 'student', label: 'Student', hint: 'Learning AI production and building a portfolio' },
  { id: 'beginner', label: 'Beginner', hint: 'Exploring AI and starting a creative journey' },
  { id: 'professional', label: 'Professional', hint: 'Filmmaker, AI artist, designer or creative' },
  { id: 'company', label: 'Company', hint: 'A team producing content or hiring AI talent' }
];

const CRAFTS: Choice[] = [
  { id: 'director', label: 'Director' },
  { id: 'producer', label: 'Producer' },
  { id: 'writer', label: 'Writer' },
  { id: 'vfx', label: 'VFX artist' },
  { id: 'editor', label: 'Editor' },
  { id: 'marketing', label: 'Marketer' },
  { id: 'founder', label: 'Founder' },
  { id: 'other', label: 'Something else' }
];

const MAKING: Choice[] = [
  { id: 'film', label: 'Short or feature film' },
  { id: 'series', label: 'Series' },
  { id: 'vfx', label: 'VFX shots' },
  { id: 'marketing', label: 'Ad campaigns' },
  { id: 'product', label: 'Product videos' },
  { id: 'music-video', label: 'Music videos' },
  { id: 'restoration', label: 'Upscale & restore' }
];

const TEAM_SIZES: Choice[] = [
  { id: 'solo', label: 'Just me' },
  { id: '2-10', label: '2–10' },
  { id: '11-50', label: '11–50' },
  { id: '51+', label: '51+' }
];

const REFERRALS: Choice[] = [
  { id: 'search', label: 'Search' },
  { id: 'social', label: 'Social media' },
  { id: 'friend', label: 'A friend' },
  { id: 'event', label: 'An event' },
  { id: 'other', label: 'Elsewhere' }
];

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED'];

function detectTimezone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
    return zone === 'Asia/Calcutta' ? 'Asia/Kolkata' : zone;
  } catch {
    return 'Asia/Kolkata';
  }
}

function currencyFor(timezone: string): string {
  if (timezone === 'Asia/Kolkata' || timezone === 'Asia/Calcutta') return 'INR';
  if (timezone === 'Asia/Dubai') return 'AED';
  if (timezone === 'Europe/London') return 'GBP';
  if (timezone.startsWith('Europe/')) return 'EUR';
  return 'USD';
}

function timezones(current: string): string[] {
  const all = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  return all.includes(current) ? all : [current, ...all];
}

export async function welcomeLoader(): Promise<WelcomeLoaderData | Response> {
  const auth = await api.authState();
  if (!auth.authenticated || !auth.user) return redirect('/signin?next=%2Fwelcome');
  return { user: auth.user, canEditStudio: ['owner', 'admin'].includes(auth.user.role || '') };
}

export async function welcomeAction({ request }: ActionFunctionArgs): Promise<WelcomeActionData | Response> {
  const form = await request.formData();
  try {
    const payload = JSON.parse(String(form.get('payload') || '{}')) as OnboardingPayload;
    const intent = readSignupIntent();
    await api.completeOnboarding(intent ? { ...payload, attribution: intent } : payload);
    clearSignupIntent();
    return redirect(intent ? pricingPathFor(intent) : '/');
  } catch (error) {
    return { error: error instanceof ApiError || error instanceof Error ? error.message : 'Could not save your setup.' };
  }
}

function ChoiceGroup({
  label,
  choices,
  selected,
  onToggle,
  multiple,
  columns
}: {
  label: string;
  choices: Choice[];
  selected: string[];
  onToggle: (id: string) => void;
  multiple?: boolean;
  columns?: number;
}) {
  return (
    <fieldset className="sa-choices" style={columns ? ({ '--cols': columns } as CSSProperties) : undefined}>
      <legend>
        {label}
        {multiple ? <small>Pick any</small> : null}
      </legend>
      <div role={multiple ? 'group' : 'radiogroup'}>
        {choices.map((choice, index) => {
          const active = selected.includes(choice.id);
          return (
            <button
              key={choice.id}
              type="button"
              role={multiple ? 'checkbox' : 'radio'}
              aria-checked={active}
              className="sa-chip"
              data-hint={choice.hint ? true : undefined}
              style={{ '--i': index } as CSSProperties}
              onClick={() => onToggle(choice.id)}
            >
              <b>{choice.label}</b>
              {choice.hint ? <span>{choice.hint}</span> : null}
              <i aria-hidden="true">✓</i>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

export default function Welcome() {
  const { user, canEditStudio } = useLoaderData() as WelcomeLoaderData;
  const actionData = useActionData() as WelcomeActionData | undefined;
  const navigation = useNavigation();
  const busy = navigation.state !== 'idle';

  const detected = useMemo(detectTimezone, []);
  const zones = useMemo(() => timezones(detected), [detected]);
  const steps = useMemo(() => (canEditStudio ? ['studio', 'you', 'work'] : ['you', 'work']), [canEditStudio]);

  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [studioName, setStudioName] = useState(user.org?.name || '');
  const [timezone, setTimezone] = useState(detected);
  const [currency, setCurrency] = useState(currencyFor(detected));
  const [name, setName] = useState(user.name || '');
  const [userType, setUserType] = useState('');
  const [craft, setCraft] = useState('');
  const [making, setMaking] = useState<string[]>([]);
  const [teamSize, setTeamSize] = useState('');
  const [referral, setReferral] = useState('');
  const [stepError, setStepError] = useState('');

  const current = steps[step];
  const last = step === steps.length - 1;
  const firstName = (name || user.name || '').split(/\s+/)[0];

  const payload: OnboardingPayload = {
    name,
    userType,
    craft,
    making,
    teamSize,
    referral,
    ...(canEditStudio ? { studio: { name: studioName, timezone, currency } } : {})
  };

  const go = (target: number) => {
    if (target > step) {
      if (current === 'studio' && !studioName.trim()) return setStepError('Give the studio a name. You can change it later in settings.');
      if (current === 'you' && !name.trim()) return setStepError('Tell us what to call you.');
    }
    setStepError('');
    setDirection(target > step ? 'forward' : 'back');
    setStep(target);
  };

  const single = (setter: (value: string) => void, value: string) => (id: string) => setter(value === id ? '' : id);

  return (
    <AuthScene
      eyebrow="Pre-production"
      headline={['Set the', 'slate.']}
      ghost="Then roll."
      lede="Three quick answers so the crew, the router and your cost estimates are tuned to the work you actually make. Everything can change later in settings."
    >
      <div className="sa-view sa-onboard">
        <div className="sa-onboard-head">
          <span className="eyebrow">
            Step {step + 1} of {steps.length}
          </span>
          <Link className="sa-link" to="/">
            Skip for now
          </Link>
        </div>
        <div className="sa-progress" aria-hidden="true" style={{ '--steps': steps.length, '--step': step + 1 } as CSSProperties}>
          <i />
        </div>

        <Form method="post" replace onSubmit={event => {
          if (!last) {
            event.preventDefault();
            go(step + 1);
          }
        }}>
          <input type="hidden" name="payload" value={JSON.stringify(payload)} />
          <FormError message={stepError || actionData?.error} />

          <div key={current} className="sa-step" data-direction={direction}>
            {current === 'studio' ? (
              <>
                <h1>Name your studio</h1>
                <p className="auth-sub">It appears on review links, invoices and every invite you send.</p>
                <div className="auth-form">
                  <label className="field">
                    <span>Studio name</span>
                    <input className="input" value={studioName} onChange={event => setStudioName(event.target.value)} autoComplete="organization" autoFocus maxLength={80} />
                  </label>
                  <label className="field">
                    <span>Time zone</span>
                    <select className="input" value={timezone} onChange={event => setTimezone(event.target.value)}>
                      {zones.map(zone => (
                        <option key={zone} value={zone}>
                          {zone.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </label>
                  <ChoiceGroup
                    label="Budget currency"
                    columns={5}
                    choices={CURRENCIES.map(code => ({ id: code, label: code }))}
                    selected={[currency]}
                    onToggle={setCurrency}
                  />
                </div>
              </>
            ) : null}

            {current === 'you' ? (
              <>
                <h1>{firstName ? `Nice to meet you, ${firstName}` : 'About you'}</h1>
                <p className="auth-sub">This shapes your crew card and which tools we put first.</p>
                <div className="auth-form">
                  <label className="field">
                    <span>What should the crew call you?</span>
                    <input className="input" value={name} onChange={event => setName(event.target.value)} autoComplete="name" autoFocus={!canEditStudio} maxLength={80} />
                  </label>
                  <ChoiceGroup label="Which describes you?" columns={2} choices={USER_TYPES} selected={[userType]} onToggle={single(setUserType, userType)} />
                  <ChoiceGroup label="Your craft" columns={4} choices={CRAFTS} selected={[craft]} onToggle={single(setCraft, craft)} />
                </div>
              </>
            ) : null}

            {current === 'work' ? (
              <>
                <h1>What are you making?</h1>
                <p className="auth-sub">We will open the right workspace first and tune estimates to it.</p>
                <div className="auth-form">
                  <ChoiceGroup
                    label="Projects"
                    multiple
                    columns={2}
                    choices={MAKING}
                    selected={making}
                    onToggle={id => setMaking(list => (list.includes(id) ? list.filter(item => item !== id) : [...list, id]))}
                  />
                  <ChoiceGroup label="Team size" columns={4} choices={TEAM_SIZES} selected={[teamSize]} onToggle={single(setTeamSize, teamSize)} />
                  <ChoiceGroup label="How did you find us?" columns={3} choices={REFERRALS} selected={[referral]} onToggle={single(setReferral, referral)} />
                </div>
              </>
            ) : null}
          </div>

          <div className="sa-onboard-actions">
            {step > 0 ? (
              <button type="button" className="app-button ghost" onClick={() => go(step - 1)} disabled={busy}>
                Back
              </button>
            ) : (
              <span />
            )}
            <button type="submit" className="app-button primary" disabled={busy}>
              {last ? (busy ? 'Saving…' : 'Open the studio →') : 'Continue →'}
            </button>
          </div>
        </Form>
      </div>
    </AuthScene>
  );
}
